import { useMutation } from '@apollo/client';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, X } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Interests } from '../../enums/interests.enum';
import { UPDATE_DRAFT_CATEGORIES_MUTATION } from '../../graphql/resolvers/draftMutations/updateDraftCategories.gql';
import { extractedCategories } from '../../helpers/extractedCategories';
import { humanize } from '../../helpers/interestsTaxonomy';
import type { DraftInterfaceState } from '../../interfaces/post.interface';
import { draftSlice } from '../../store/draft.store';
import ButtonComponent from '../UI/Button/Button.component';
import './categories.scss';

interface CategoriesProps {
  draftId: string;
  selectedCategories: string[];
}

type AutoCompleteCategories = { label: string };

export default function CategoriesAutocomplete(props: CategoriesProps) {
  const { draftId } = props;
  const dispatch = useDispatch();
  const { categories } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );
  const {
    RESET_DRAFT_CATEGORIES,
    POPULATE_CATEGORIES,
    SAVE_DRAFT_CATEGORIES,
    SAVE_DRAFT_START_STORE_ACTION,
    IS_EDITING_DRAFT_STORE_ACTION,
  } = draftSlice.actions;
  const [pendingCategories, setPendingCategories] = React.useState<string[]>(
    [],
  );
  const [savedCategories, setSavedCategories] = React.useState<string[]>([]);
  const { draft } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );

  React.useEffect(() => {
    if (draft) {
      setSavedCategories(draft.categories ?? []);
      dispatch(POPULATE_CATEGORIES(draft.categories));
    }
  }, [draft]);

  const availableCategories = React.useMemo(() => {
    const selected = new Set([...savedCategories, ...pendingCategories]);
    return Object.values(Interests).filter((value) => !selected.has(value));
  }, [savedCategories, pendingCategories]);

  const handleSelectCategory = (category: string) => {
    setPendingCategories((prev) => [...prev, category]);
  };

  const handleRemovePending = (category: string) => {
    setPendingCategories((prev) => prev.filter((c) => c !== category));
  };

  const [updateDraftCategories, { loading }] = useMutation(
    UPDATE_DRAFT_CATEGORIES_MUTATION,
    {
      variables: { input: undefined },
      onError: (ex) => {
        console.error('useMutation error =>', ex);
      },
    },
  );

  const handleSaveCategories = () => {
    if (pendingCategories.length === 0) return;

    const extracted = extractedCategories(
      pendingCategories.map((label) => ({ label })) as AutoCompleteCategories[],
    );

    dispatch(SAVE_DRAFT_CATEGORIES(extracted));
    dispatch(IS_EDITING_DRAFT_STORE_ACTION(true));
    dispatch(SAVE_DRAFT_START_STORE_ACTION(true));

    const allCategories = [...(categories as string[]), ...extracted];
    updateDraftCategories({
      variables: { input: { categories: allCategories, id: draftId } },
    })
      .then(() => {
        dispatch(IS_EDITING_DRAFT_STORE_ACTION(false));
        dispatch(SAVE_DRAFT_START_STORE_ACTION(false));
        dispatch(RESET_DRAFT_CATEGORIES());
        dispatch(SAVE_DRAFT_CATEGORIES(allCategories));
        setPendingCategories([]);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <div
      className="CATEGORIES__AUTOCOMPLETE__CONTAINER"
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.5rem',
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className="categories-trigger">
              <span>Select categories</span>
              <ChevronDown size={16} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="categories-menu"
              sideOffset={4}
              align="start"
            >
              {availableCategories.length === 0 ? (
                <DropdownMenu.Item
                  className="categories-menu__item categories-menu__item--disabled"
                  disabled
                >
                  All categories selected
                </DropdownMenu.Item>
              ) : (
                availableCategories.map((cat) => (
                  <DropdownMenu.Item
                    key={cat}
                    className="categories-menu__item"
                    onSelect={() => handleSelectCategory(cat)}
                  >
                    {humanize(cat)}
                  </DropdownMenu.Item>
                ))
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <ButtonComponent
          size="sm"
          variant="primary"
          onClick={handleSaveCategories}
          disabled={pendingCategories.length === 0}
          style={{ marginRight: '0.5rem' }}
        >
          Save
        </ButtonComponent>
      </div>

      {pendingCategories.length > 0 && (
        <div className="categories-pending">
          {pendingCategories.map((cat) => (
            <span
              key={cat}
              className="categories-chip categories-chip--pending"
            >
              {humanize(cat)}
              <button
                type="button"
                onClick={() => handleRemovePending(cat)}
                aria-label={`Remove ${humanize(cat)}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
